import React, { useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, ArrowUp, Loader2, Check } from "lucide-react";
import { createRecorder } from "../services/audio";
import { transcribe } from "../services/transcription";
import { createMemory } from "../services/memories";
import { playCaughtFeedback } from "../services/feedback";
import { getSettings } from "../services/settings";
import { useT } from "../services/i18n";

function Illustration({ className = "" }) {
  return (
    <img
      src="/butterfly-nobg.png"
      alt="rememor"
      draggable="false"
      className={`pointer-events-none w-64 max-w-[78vw] select-none ${className}`}
    />
  );
}

const TOO_SHORT = {
  fr: "Trop court — maintiens un peu plus longtemps pour dicter.",
  en: "Too short — hold a little longer to dictate.",
  ru: "Слишком коротко — удерживайте чуть дольше.",
};

export default function CaptureScreen() {
  const { t, lang } = useT();
  const [text, setText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | recording | transcribing | sending
  const [flying, setFlying] = useState(false);
  const [error, setError] = useState("");
  const [locked, setLocked] = useState(false);

  const recorderRef = useRef(null);
  const audioBlobRef = useRef(null);
  const holdingRef = useRef(false);
  const startingRef = useRef(false);
  const startYRef = useRef(null);

  const hasText = text.trim().length > 0;

  // Affiche une erreur qui s'efface seule (n'empêche jamais de réenregistrer).
  const flashError = useCallback((msg) => {
    setError(msg);
    window.setTimeout(() => setError(""), 2600);
  }, []);

  const startRecording = useCallback(async () => {
    // Garde basée sur des refs (synchrones) : ne reste jamais bloqué sur un état périmé.
    if (startingRef.current || recorderRef.current) return;
    setError("");
    holdingRef.current = true;
    startingRef.current = true;
    try {
      const recorder = createRecorder();
      await recorder.start();
      startingRef.current = false;
      if (!holdingRef.current) {
        // Relâché pendant l'initialisation (doigt qui fourche) -> on annule sans erreur.
        await recorder.stop();
        setStatus("idle");
        return;
      }
      recorderRef.current = recorder;
      setStatus("recording");
    } catch (e) {
      flashError(t("cap_err_mic"));
      setStatus("idle");
      holdingRef.current = false;
      startingRef.current = false;
      recorderRef.current = null;
    }
  }, [t, flashError]);

  const stopRecording = useCallback(async () => {
    holdingRef.current = false;
    const recorder = recorderRef.current;
    if (!recorder) {
      // Rien démarré ou annulé pendant l'init : on revient à l'état neutre.
      setStatus("idle");
      return;
    }
    recorderRef.current = null;

    setStatus("transcribing");
    try {
      const blob = await recorder.stop();
      if (!blob || blob.size < 1500) {
        flashError(TOO_SHORT[lang] || TOO_SHORT.fr);
        setStatus("idle");
        return;
      }
      audioBlobRef.current = blob;
      const transcript = await transcribe(blob);
      setText((prev) => (prev ? `${prev} ${transcript}` : transcript).trim());
    } catch (e) {
      flashError(t("cap_err_transcribe"));
    } finally {
      setStatus("idle");
    }
  }, [t, lang, flashError]);

  const onPressStart = (clientY) => {
    startYRef.current = clientY;
    startRecording();
  };
  const onPressMove = (clientY) => {
    if (locked || !holdingRef.current || startYRef.current == null) return;
    if (startYRef.current - clientY > 60) {
      setLocked(true);
      try {
        if (navigator.vibrate) navigator.vibrate(12);
      } catch (e) {
        /* no-op */
      }
    }
  };
  const onPressEnd = () => {
    startYRef.current = null;
    if (locked) return;
    stopRecording();
  };
  const finishLocked = async () => {
    setLocked(false);
    await stopRecording();
  };

  const handleSend = useCallback(async () => {
    if (!hasText || status === "sending") return;
    const transcript = text.trim();
    const audioBlob = audioBlobRef.current;
    setText("");
    audioBlobRef.current = null;
    setFlying(true);
    playCaughtFeedback();
    setStatus("sending");

    createMemory({ transcript, audioBlob })
      .catch(() => {
        setError(t("cap_err_save"));
        setText(transcript);
        audioBlobRef.current = audioBlob;
      })
      .finally(() => setStatus("idle"));

    window.setTimeout(() => setFlying(false), 1400);
  }, [hasText, status, text, t]);

  const preventContext = (e) => e.preventDefault();
  const recording = status === "recording";
  const transcribing = status === "transcribing";
  const leftHanded = getSettings().hand === "left";

  return (
    <div className="flex min-h-[100dvh] flex-col" data-testid="capture-screen">
      <header className="flex items-center justify-between px-6 pt-[max(20px,env(safe-area-inset-top))] pb-2">
        <h1 className="brand text-2xl font-bold text-[var(--rmr-text)]">
          rememor<span className="text-[var(--rmr-accent)]">.ai</span>
        </h1>
        <button
          data-testid="settings-open-btn"
          onClick={() => window.dispatchEvent(new CustomEvent("open-settings"))}
          className="grid h-10 w-10 place-items-center rounded-full text-[var(--rmr-text-2)] transition-colors hover:bg-[var(--rmr-surface)] hover:text-[var(--rmr-accent)]"
          aria-label={t("cap_settings_aria")}
        >
          <SettingsGear />
        </button>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <AnimatePresence mode="wait">
          {flying ? (
            <motion.div
              key="flying"
              initial={{ y: 0, opacity: 1, scale: 1 }}
              animate={{ y: -160, opacity: 0, scale: 0.7, rotate: 6 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            >
              <Illustration />
            </motion.div>
          ) : (
            <motion.div
              key="resting"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Illustration className="rmr-float" />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {flying && (
            <motion.p
              key="caught"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-3 text-base font-medium text-[var(--rmr-accent)]"
              data-testid="caught-message"
            >
              {t("cap_caught")}
            </motion.p>
          )}
        </AnimatePresence>

        {!flying && (
          <p className="mt-6 max-w-[18rem] text-lg leading-relaxed text-[var(--rmr-text-2)]">
            {recording
              ? locked
                ? t("cap_listening_locked")
                : t("cap_listening")
              : transcribing
                ? t("cap_transcribing")
                : t("cap_prompt")}
          </p>
        )}
      </main>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mx-6 mb-2 rounded-2xl bg-[var(--rmr-danger-bg)] px-4 py-2 text-center text-sm text-[var(--rmr-danger)]"
            data-testid="capture-error"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="px-4 pb-[calc(88px+env(safe-area-inset-bottom))]">
        {locked ? (
          <div
            className="flex items-center gap-2 rounded-[28px] border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-2 pl-4 shadow-[0_8px_30px_rgba(110,102,214,0.08)]"
            data-testid="lock-bar"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-[var(--rmr-danger)]">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[var(--rmr-danger)]" />
              {t("cap_locked")}
            </span>
            <span className="flex-1 truncate text-sm text-[var(--rmr-text-2)]">
              {t("cap_hands_free")}
            </span>
            <button
              data-testid="lock-finish-btn"
              onClick={finishLocked}
              className="flex items-center gap-1 rounded-full bg-[var(--rmr-accent)] px-4 py-2.5 text-sm font-semibold text-white transition-transform active:scale-95"
            >
              <Check size={16} /> {t("cap_finish")}
            </button>
          </div>
        ) : (
          <div
            className={`flex items-end gap-2 rounded-[28px] border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-2 shadow-[0_8px_30px_rgba(110,102,214,0.08)] ${
              leftHanded ? "flex-row-reverse" : ""
            }`}
            data-testid="composer"
          >
            <textarea
              data-testid="composer-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={1}
              placeholder={t("cap_placeholder")}
              className="max-h-32 flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] text-[var(--rmr-text)] outline-none placeholder:text-[var(--rmr-text-3)]"
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
              }}
            />

            {hasText ? (
              <button
                data-testid="send-btn"
                onClick={handleSend}
                disabled={status === "sending"}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--rmr-accent)] text-white transition-transform active:scale-90 disabled:opacity-60"
                aria-label={t("cap_send_aria")}
              >
                {status === "sending" ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <ArrowUp size={20} strokeWidth={2.5} />
                )}
              </button>
            ) : (
              <button
                data-testid="mic-btn"
                onMouseDown={(e) => onPressStart(e.clientY)}
                onMouseMove={(e) => onPressMove(e.clientY)}
                onMouseUp={onPressEnd}
                onMouseLeave={() => {
                  if (!locked && holdingRef.current) onPressEnd();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  onPressStart(e.touches[0].clientY);
                }}
                onTouchMove={(e) => onPressMove(e.touches[0].clientY)}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  onPressEnd();
                }}
                onContextMenu={preventContext}
                disabled={transcribing}
                style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
                className={`grid h-11 w-11 shrink-0 select-none place-items-center rounded-full transition-all active:scale-90 ${
                  recording
                    ? "scale-110 bg-[var(--rmr-accent)] text-white shadow-[0_0_0_8px_rgba(110,102,214,0.18)]"
                    : "bg-[var(--rmr-chip)] text-[var(--rmr-accent)]"
                }`}
                aria-label={t("cap_hold_aria")}
              >
                {transcribing ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Mic size={20} strokeWidth={2.2} />
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsGear() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
